// src/components/PropertyCard/PropertyCard.js
import React, { useState } from 'react'
import {
  Card,
  CardMedia,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  Rating
} from '@mui/material'
import {
  Favorite,
  FavoriteBorder,
  LocationOn,
  Home,
  Bathtub,
  DirectionsCar,
  AspectRatio
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { propertiesAPI } from '../../services/api'

const PropertyCard = ({ property, onFavoriteToggle }) => {
  const navigate = useNavigate()
  const [isFavorited, setIsFavorited] = useState(
    property?.is_favorited || false
  )

  const handleFavoriteClick = async (e) => {
    e.stopPropagation()
    try {
      const response = await propertiesAPI.toggleFavorite(property.id)

      // Atualizar estado local
      if (response.data.favorited !== undefined) {
        setIsFavorited(response.data.favorited)
      } else {
        // Fallback: toggle local
        setIsFavorited(!isFavorited)
      }

      // Notificar componente pai se existir callback
      if (onFavoriteToggle) {
        onFavoriteToggle(property.id)
      }
    } catch (error) {
      console.error('Erro ao favoritar:', error)
    }
  }

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price)
  }

  const getPropertyTypeLabel = (type) => {
    const types = {
      casa: 'Casa',
      apartamento: 'Apartamento',
      terreno: 'Terreno',
      comercial: 'Comercial',
      rural: 'Rural'
    }
    return types[type] || type
  }

  const firstImage = property?.images?.[0]?.image || '/placeholder-house.jpg'

  return (
    <Card
      sx={{
        width: '100%',
        minHeight: 450,
        maxHeight: 450,
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        transition: 'all 0.3s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4
        }
      }}
      onClick={() => navigate(`/imoveis/${property.id}`)}
    >
      {/* Imagem com altura fixa */}
      <Box sx={{ position: 'relative', height: 200, overflow: 'hidden' }}>
        <CardMedia
          component='img'
          image={firstImage}
          alt={property?.title}
          sx={{
            objectFit: 'cover',
            width: '100%',
            height: '100%'
          }}
        />

        {/* Chips de Negociação e Aceita Troca */}
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            display: 'flex',
            gap: 1
          }}
        >
          {property?.accepts_trade && (
            <Chip
              label='Aceita troca'
              size='small'
              sx={{
                bgcolor: '#FF9800',
                color: 'white',
                fontWeight: 'bold'
              }}
            />
          )}
          <Chip
            label={property?.transaction_type === 'venda' ? 'Venda' : 'Aluguel'}
            size='small'
            color={property?.transaction_type === 'venda' ? 'success' : 'info'}
            sx={{ fontWeight: 'bold' }}
          />
        </Box>

        {/* Botão de Favorito */}
        <IconButton
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            bgcolor: 'white',
            '&:hover': { bgcolor: 'grey.100' }
          }}
          onClick={handleFavoriteClick}
        >
          {isFavorited ? <Favorite color='error' /> : <FavoriteBorder />}
        </IconButton>
      </Box>

      <CardContent
        sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2 }}
      >
        {/* Título com altura fixa (2 linhas) */}
        <Typography
          variant='h6'
          component='h3'
          gutterBottom
          sx={{
            fontWeight: 'bold',
            fontSize: '1rem',
            height: '2.8em',
            lineHeight: '1.4em',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            mb: 1
          }}
        >
          {property?.title}
        </Typography>

        {/* Localização */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 1.5,
            minHeight: '24px'
          }}
        >
          <LocationOn sx={{ fontSize: 18, color: 'text.secondary', mr: 0.5 }} />
          <Typography variant='body2' color='text.secondary' noWrap>
            {property?.city}, {property?.state}
          </Typography>
        </Box>

        {/* Características em linha */}
        <Box sx={{ display: 'flex', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Home sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant='body2' color='text.secondary'>
              {property?.bedrooms}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Bathtub sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant='body2' color='text.secondary'>
              {property?.bathrooms}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <DirectionsCar sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant='body2' color='text.secondary'>
              {property?.garage_spaces}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AspectRatio sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant='body2' color='text.secondary'>
              {property?.area}m²
            </Typography>
          </Box>
        </Box>

        {/* Rating do Vendedor */}
        {property?.owner_rating > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
            <Rating
              value={property?.owner_rating}
              readOnly
              size='small'
              precision={0.1}
            />
            <Typography variant='caption' sx={{ ml: 0.5 }}>
              ({property.owner_reviews_count})
            </Typography>
          </Box>
        )}

        {/* Espaçador flexível */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Preço */}
        <Typography
          variant='h5'
          color='primary'
          sx={{
            fontWeight: 'bold',
            mt: 'auto'
          }}
        >
          {property?.price_on_request ? 'Valor a Combinar' : formatPrice(property?.price)}
        </Typography>

        {/* Tipo de imóvel */}
        <Typography variant='caption' color='text.secondary'>
          {getPropertyTypeLabel(property?.property_type)}
        </Typography>
      </CardContent>
    </Card>
  )
}

export default PropertyCard
